/**
 * HVP Cache Manager - sessionStorage persistence
 *
 * Save/restore/clear of the per-product cache snapshot, with version,
 * product and TTL validation and the debounced save scheduler.
 *
 * Split out of cache-manager.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCacheManager.prototype by
 * cache-manager.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CacheSessionStoreMethods = {
    /**
     * Set the product ID (for sessionStorage key)
     * Call this when the product ID becomes available
     *
     * @param {number} productId - Product ID
     */
    setProductId(productId) {
        this.productId = productId;

        // Try to load from sessionStorage with new product ID
        if (this.sessionStorageEnabled && this.productId) {
            this.loadFromSessionStorage();
        }
    },

    /**
     * Save cache to sessionStorage
     *
     * Persists the cache to sessionStorage for instant restoration on page reload.
     * Includes version and timestamp for validation.
     */
    saveToSessionStorage() {
        if (!this.sessionStorageEnabled || !this.productId) {
            return false;
        }

        try {
            const data = {
                version: this.cacheVersion,
                productId: this.productId,
                timestamp: Date.now(),
                cache: Array.from(this.cache.entries()),
                metadata: Array.from(this.metadata.entries())
            };

            const key = `hvp_cache_${this.productId}`;
            const jsonData = JSON.stringify(data);

            // Check if data is too large (sessionStorage limit ~5MB)
            if (jsonData.length > 4 * 1024 * 1024) { // 4MB limit
                this.log('[SessionStorage] Data too large, skipping save');
                return false;
            }

            sessionStorage.setItem(key, jsonData);
            this.stats.sessionSaves++;
            this.log('[SessionStorage] Saved cache', this.cache.size, 'entries');
            return true;

        } catch (error) {
            // sessionStorage might be full or disabled
            window.hvpData && window.hvpData.debug && console.warn('[HVP Cache] SessionStorage save failed:', error.message);
            return false;
        }
    },

    /**
     * Load cache from sessionStorage
     *
     * Restores the cache from sessionStorage if valid.
     * Validates version, productId, and timestamp.
     *
     * @returns {boolean} Whether cache was restored
     */
    loadFromSessionStorage() {
        if (!this.sessionStorageEnabled || !this.productId) {
            return false;
        }

        try {
            const key = `hvp_cache_${this.productId}`;
            const cached = sessionStorage.getItem(key);

            if (!cached) {
                this.log('[SessionStorage] No cached data found');
                return false;
            }

            const data = JSON.parse(cached);

            // Validate version
            if (data.version !== this.cacheVersion) {
                this.log('[SessionStorage] Version mismatch, clearing');
                sessionStorage.removeItem(key);
                return false;
            }

            // Validate productId
            if (data.productId !== this.productId) {
                this.log('[SessionStorage] Product ID mismatch, clearing');
                sessionStorage.removeItem(key);
                return false;
            }

            // Check TTL
            const age = Date.now() - data.timestamp;
            if (age > this.sessionStorageTtl) {
                this.log('[SessionStorage] Cache expired', Math.round(age / 1000), 'seconds old');
                sessionStorage.removeItem(key);
                return false;
            }

            // Restore cache and metadata
            this.cache = new Map(data.cache);
            this.metadata = new Map(data.metadata);
            this.stats.sessionRestores++;

            this.log('[SessionStorage] Restored cache', this.cache.size, 'entries from', Math.round(age / 1000), 'seconds ago');
            return true;

        } catch (error) {
            // Clear corrupted sessionStorage entry to prevent repeated failures
            const key = `hvp_cache_${this.productId}`;
            try {
                sessionStorage.removeItem(key);
                window.hvpData && window.hvpData.debug && console.warn('[HVP Cache] SessionStorage load failed, cleared corrupted entry:', error.message);
            } catch (clearError) {
                window.hvpData && window.hvpData.debug && console.warn('[HVP Cache] SessionStorage load failed:', error.message);
            }
            return false;
        }
    },

    /**
     * Clear sessionStorage cache
     */
    clearSessionStorage() {
        if (this.productId) {
            const key = `hvp_cache_${this.productId}`;
            sessionStorage.removeItem(key);
            this.log('[SessionStorage] Cleared');
        }
    },

    /**
     * Schedule sessionStorage save (debounced)
     * Called automatically after cache changes
     */
    scheduleSave() {
        if (!this.sessionStorageEnabled || !this.productId) {
            return;
        }

        // Clear existing timeout
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
        }

        // Debounce save by 2 seconds
        this.saveTimeout = setTimeout(() => {
            this.saveToSessionStorage();
        }, 2000);
    },
};

export default CacheSessionStoreMethods;
